import {getString} from '../resources/strings'
import {Class} from '../utils'
import QueryResult from '../QueryResult'
import {getWKTParser} from '../util/getWKTParser'

/**
 * Base class for all searchers
 * @class Searcher
 * @constructs Searcher
 * @param {Object} options
 * @param [options.onSelect] {Searcher~selectCallback} Function to call when a result is selected by the user.
 * @param [options.searchDelay=0] {int} Delay in ms before executing the search
 * @param [options.matchesPhrase="matcher"] {string} Phrase used to display matches.
 * @param [options.usesGeoFunctions=false] {boolean} Does the implementation need the geo functions
 * @param [options.iconURI] {boolean} Does the implementation need the geo functions
 * @param [options.blankBehavior="search"] {string} "none"|"search"
 */

//noinspection JSCommentMatchesSignature
export default Class(
  /** @lends Searcher# */
  {

    //Private members
    //Dont touch
    _id: null,
    _onSelectCallback: null,
    detailHandlerDefs: null,
    customButtonDefs: null,
    onNewQueryHandlers: null,
    iconURI: null,
    _searchDelay: null,
    _sources: null,
    _matchesPhrase: null,
    allowDetails: false,

    toJSON: function () {
      return {
        id: this._id
      };
    },

    Searcher: function (options) {
      this.customButtonDefs = {"any": []};
      this.detailHandlerDefs = {"any": []};
      //this.onNewQueryHandlers  = [];
      this._searchDelay = 0;
      this._sources = [];//[{source: this.CLASS_NAME, types: []}];
      this._matchesPhrase = getString("matches");
      this._onSelectCallback = function () {
      };
      this.blankBehavior = "search"; //none, search
      this.allowDetails = false;

      if (options !== undefined) {
        if (typeof options.source !== 'undefined') {
          this._sources = [{source: options.source, types: []}];
        }
        if (typeof options.onSelect !== 'undefined') {
          this._onSelectCallback = options.onSelect;
        }
        if (options.matchesPhrase) {
          this._matchesPhrase = options.matchesPhrase;
        }
        if (options.searchDelay) {
          this._searchDelay = options.searchDelay;
        }
        if (options.iconURI) {
          this.iconURI = options.iconURI;
        }
        if (options.blankBehavior) {
          this.blankBehavior = options.blankBehavior;
        }
        if (options.allowDetails) {
          this.allowDetails = options.allowDetails;
        }
        if (options.usesGeoFunctions) {
          this.wktParser = getWKTParser();
        }
      }
      this._id = this.CLASS_NAME + '_' + Math.floor(Math.random() * 999999999);
    },
    /**
     * A search result item created by a searcher
     * @typedef {Object} Searcher.Result
     * @property {string} title The title as displayed in the search result
     * @property {string} description The description as displayed in the search result
     * @property {object} geometry geojson
     * @property {Searcher} searcher
     * @property {object} data Searcher specific data - See each searcher for details
     */

    ready: function () {
      return Promise.resolve(true);
    },

    /**
     * @private
     */
    _fetchData: function (query) {
      //Returns Promise. Calls caller if caller is set
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          this.fetchData(
            query, {
              fetchSuccess: (queryResult) => {
                resolve(queryResult);
              },
              fetchError: (message) => {
                reject(new Error(message));
              }
            });
        }, this._searchDelay);
      });
    },

    /**
     * This method is called by the controller when the driver should fetch data. <br>
     * This method MUST be implemented by all searchers <br>
     * A call to this method MUST result in either a call to {@link Controller}.fetchSuccess({@link QueryResult}) or caller.fetchError()
     * @param {Controller} caller
     * */
    fetchData: function () {
      //Return error if fetchData hasn't been implemented in driver
      throw ("Bad driver implementation in " + this.getId() + ". Method fetchData MUST be implemented");
    },


    /**
     * @private
     */
    _onSelect: function (result) {
      return this.onSelect(result);
    },

    completeResult: function (result) {
      result.isComplete = true;
      return Promise.resolve(result);
    },

    /**
     * This method is called by the controller when a result is selected. <br>
     * This method MAY be implemented by searchers. <br>
     * Implementations MUST call this._onSelectCallback(result)
     * @param {Searcher.Result} result
     */
    onSelect: function (result) {
      if (typeof result.newquery === 'undefined') {
        this._onSelectCallback(result);
      }
    },

    registerType: function (sourceName, type) {
      let source = this.getSource(sourceName);
      source.types.push(type);
    },

    getSource: function (sourceName) {
      for (let i = 0; i < this._sources.length; i++) {
        if (this._sources[i].source.toLowerCase() === sourceName.toLowerCase()) {
          return this._sources[i];
        }
      }
      let source = {source: sourceName, types: []}
      this._sources.push(source);
      return source;
    },

    hasSource: function (sourceName) {
      for (let i = 0; i < this._sources.length; i++) {
        if (this._sources[i].source.toLowerCase() === sourceName.toLowerCase()) {
          return true;
        }
      }
      return false;
    },

    getSources: function () {
      return this._sources;
    },

    hasType: function (testType) {
      for (let i = 0; i < this._sources.length; i++) {
        let thisSource = this._sources[i];
        for (let j = 0; j < thisSource.types.length; j++) {
          let thisType = thisSource.types[j];
          if (thisType.toLowerCase() === testType.toLowerCase()) {
            return true;
          }
        }
      }
      return false;
    },

    getTypes: function () {
      return this._types;
    },

    /**
     * @private
     */
    getId: function () {
      return this._id;
    },

    /**
     * Create a {QueryResult}.
     * @returns {QueryResult}
     *
     */
    createQueryResult: function () {
      return new QueryResult(this);
    },

    /**
     * Convert a wkt string to a geojson object
     * @param {string} wkt
     * @returns {GeoJsonObject}
     */
    translateWktToGeoJsonObject: function (wkt) {
      return this.wktParser.parse(wkt);
      //return null;
      //let jtsGeometry =   _septimaSearcherJstsWktReader.read(wkt);
      //return _septimaSearcherJstsGeoJSONWriter.write(jtsGeometry);
    },

    translateGeoJsonObjectToWkt: function (geoJsonObject) {
      return this.wktParser.convert(geoJsonObject);
      //return null;
      //let jtsGeometry =   _septimaSearcherJstsGeoJSONReader.read(geoJsonObject);
      //return _septimaSearcherJstsWktWriter.write(jtsGeometry);
    },

    get: function () {
      return new Promise((resolve, reject) => {
        reject(new Error('Whoops! get(id) is not implemented in ' + this.CLASS_NAME));
      });
    },

    sq: function () {
      return new Promise((resolve) => {
        resolve(this.createQueryResult());
      });
    },

    getSuggestions: function () {
      return;
      //add suggestion for each type matching querystring
      /*
		if (this._types.length > 1){
			let testQueryString = queryString.toLowerCase();
			for (let i=0; i<this._types.length; i++){
				let type = this._types[i];
//	    		if (type.toLowerCase().indexOf(testQueryString) === 0 || this.source.toLowerCase().indexOf(queryString)=== 0){
				if (type.toLowerCase().indexOf(testQueryString) === 0){
					queryResult.addSuggestion(this.source, type, "");
				}
			}
		}
		*/
    },

    hasdetailHandlerDefs: function (result) {
      if (typeof result.detailHandlerDefs === 'undefined') {
        result.detailHandlerDefs = this.getdetailHandlerDefs(result);
      }
      return result.detailHandlerDefs.length > 0;
    },

    isNewQuery: function (result) {
      return typeof result.newquery !== 'undefined' && result.newquery !== null;
    },

    //This function may be overridden by implementations of search
    //Implementations may want to return this.customButtonDefs.any
    getdetailHandlerDefs: function (result) {
      if (this.isNewQuery(result)) {
        return [];
      } else {
        let tentativeHandlerDefs = [];
        if (typeof result.type === 'undefined' || typeof this.detailHandlerDefs[result.type.toLowerCase()] === 'undefined') {
          tentativeHandlerDefs = this.detailHandlerDefs.any;
        } else {
          tentativeHandlerDefs = this.detailHandlerDefs[result.type.toLowerCase()].concat(this.detailHandlerDefs.any);
        }
        let handlerDefs = [];
        for (let i = 0; i < tentativeHandlerDefs.length; i++) {
          let tentativeHandlerDef = tentativeHandlerDefs[i];
          if (typeof tentativeHandlerDef.isApplicable === 'undefined') {
            handlerDefs.push(tentativeHandlerDef);
          } else if (tentativeHandlerDef.isApplicable(result)) {
            handlerDefs.push(tentativeHandlerDef);
          }
        }
        return this.getDetailHandlersForResult(result).concat(handlerDefs);
      }
    },

    //This function may be overridden by implementations of search
    getDetailHandlersForResult: function () {
      return [];
    },

    //This function may be overridden by implementations of search
    //Implementations may want to return this.customButtonDefs.any
    getCustomButtonDefs: function (result) {
      let buttonDefs = [];
      if (this.isNewQuery(result)) {
        return buttonDefs;
      } else {
        let tentativeButtonDefs = [];
        if (typeof result.type === 'undefined' || typeof this.customButtonDefs[result.type.toLowerCase()] === 'undefined') {
          tentativeButtonDefs = this.getCustomButtonsForResult(result).concat(this.customButtonDefs.any);
        } else {
          tentativeButtonDefs = this.getCustomButtonsForResult(result).concat(this.customButtonDefs[result.type.toLowerCase()].concat(this.customButtonDefs.any));
        }
        for (let i = 0; i < tentativeButtonDefs.length; i++) {
          let tentativeButtonDef = tentativeButtonDefs[i];
          if (typeof tentativeButtonDef.isApplicable === 'undefined') {
            buttonDefs.push(tentativeButtonDef);
          } else if (tentativeButtonDef.isApplicable(result)) {
            buttonDefs.push(tentativeButtonDef);
          }
        }
        return buttonDefs;
      }
    },

    //This function may be overridden by implementations of search
    getCustomButtonsForResult: function () {
      return [];
    },

    //{"buttonText": text, "buttonImage": imageUri, "handler": function(result, detailsContent)[, more: true|false]};
    //handler must return a thenable resolving to detail contents

    addDetailHandlerDef: function (detailHandlerDef, type) {
      if (typeof type === 'undefined') {
        this.detailHandlerDefs.any.push(detailHandlerDef);
      } else {
        const lowerType = type.toLowerCase();
        if (typeof this.detailHandlerDefs[lowerType] === 'undefined') {
          this.detailHandlerDefs[lowerType] = [detailHandlerDef];
        } else {
          this.detailHandlerDefs[lowerType].push(detailHandlerDef);
        }
      }
    },

    /**
     * Definition of a button, icon, or link which is presented together with search results
     * @typedef {Object} Searcher.CustomButtonDef
     * @property {string} buttonText Text or tooltip
     * @property {string} buttonImage URL of image (20*20)
     * @property {function} callBack Called when icon is clicked (callBack(result))
     * @property {string} [target] Name of target
     */

    /**
     * Displays an icon next to each result.
     * @param {Searcher.CustomButtonDef} customButtonDef
     * */
    addCustomButtonDef: function (customButtonDef, type) {
      //fix the callback to make sure the result is completed before the callback is called
      const originalCallback = customButtonDef.callBack;
      customButtonDef.callBack = async (result) => {
        if (result.isComplete !== 'undefined' && result.isComplete === false) {
          let completedResult = await result.searcher.completeResult(result);
          return originalCallback(completedResult);
        } else {
          return originalCallback(result);
        }
      };
      //Put in correct array of buttondefs (any or target)
      if (typeof type === 'undefined') {
        this.customButtonDefs.any.push(customButtonDef);
      } else {
        const lowerType = type.toLowerCase();
        if (typeof this.customButtonDefs[lowerType] === 'undefined') {
          this.customButtonDefs[lowerType] = [customButtonDef];
        } else {
          this.customButtonDefs[lowerType].push(customButtonDef);
        }
      }
    },

    getSearchStrategy: function(query){
      let isBlank = (query.queryString === '');
      if (isBlank){
        if (this.blankBehavior === 'none'){
          return 'nothing';
        }else{
          let hasTarget = query.hasTarget;
          let hasMultipleTypes = ((this._sources.length > 1) || (this._sources[0].types.length > 1));
          if (!hasTarget && hasMultipleTypes){
            return 'listtypes'
          }
        }
      }
      return 'search';
    },

    getListStrategy: function (query, hitcount) {
      let strategy;
      if (query.type == 'list.force') {
        strategy = 'cut';
      } else {
        if (query.queryString === '') {
          strategy = 'newquery';
        } else {
          if (hitcount <= query.limit) {
            strategy = 'showall';
          } else {
            strategy = 'newquery';
          }
        }
      }
      let isMixed;
      isMixed = !query.hasTarget && query.type == 'list';
      const count = Math.min(hitcount, query.limit);
      let show;
      if (strategy === 'cut' || strategy === 'showall') {
        show = true;
      } else {
        show = false;
      }
      let showResultIcon;
      if (query.type == 'list') {
        showResultIcon = true;
      }
      let showFolderIcon;
      if (strategy === 'newquery' && query.queryString.length > 0) {
        showFolderIcon = true;
      } else {
        showFolderIcon = false;
      }
      return {
        strategy: strategy,
        isMixed: isMixed,
        count: count,
        show: show,
        showResultIcon: showResultIcon,
        showFolderIcon: showFolderIcon
      };
    },

    CLASS_NAME: 'Septima.Search.Searcher'

  });
